import React, { useEffect, useMemo, useState } from 'react';
import { adminGetMembers, adminGetCrmProfile, adminUpdateCrm, adminMemberCredit, adminMemberBlock, adminMemberDelete } from '../../lib/api';
import { toAuWhatsApp } from './adminConstants';
import { useToast } from '../../hooks/use-toast';
import {
  Users, Search, X, Loader2, MessageCircle, Smartphone, Mail, Star,
  Wallet, Package, BadgePercent, StickyNote, Plus, Tag, Gift, ShieldOff, ShieldCheck as ShieldOk, Trash2,
} from 'lucide-react';
const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TAG_SUGGESTIONS = ['VIP', 'Regular', 'Affiliate', 'Wholesale', 'Watch'];
const SEGMENTS = [
  { key: 'all', label: 'All' },
  { key: 'vip', label: 'VIP $300+' },
  { key: 'credit', label: 'Has credit' },
  { key: 'new', label: 'New (30d)' },
  { key: 'quiet', label: 'Gone quiet (60d+)' },
  { key: 'guests', label: 'Guest buyers' },
];

const Chip = ({ children, cls = '' }) => (
  <span className={`text-[10px] tracking-[0.1em] uppercase border rounded-full px-2 py-0.5 ${cls}`}>{children}</span>
);

export default function CrmPanel() {
  const { toast } = useToast();
  const [data, setData] = useState({ members: [], guests: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [creditAmt, setCreditAmt] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [bday, setBday] = useState({ day: '', month: '' });

  const load = () => adminGetMembers().then(setData).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openProfile = async (id) => {
    setOpenId(id); setProfile(null); setProfileLoading(true);
    try {
      const p = await adminGetCrmProfile(id);
      setProfile(p); setNotes(p.profile.crmNotes || '');
      const [m, d] = (p.profile.birthday || '').split('-');
      setBday({ day: d ? String(Number(d)) : '', month: m ? String(Number(m)) : '' });
    } catch (e) { toast({ title: 'Could not load profile', variant: 'destructive' }); setOpenId(null); }
    finally { setProfileLoading(false); }
  };

  const saveTags = async (tags) => {
    setProfile((p) => ({ ...p, profile: { ...p.profile, tags } }));
    try { await adminUpdateCrm(openId, { tags }); load(); } catch (e) { toast({ title: 'Could not save tags', variant: 'destructive' }); }
  };
  const addTag = (t) => {
    const tag = (t || '').trim();
    if (!tag || (profile.profile.tags || []).includes(tag)) return;
    setTagInput('');
    saveTags([...(profile.profile.tags || []), tag]);
  };
  const saveNotes = async () => {
    setBusy(true);
    try { await adminUpdateCrm(openId, { crmNotes: notes }); toast({ title: 'Notes saved' }); }
    catch (e) { toast({ title: 'Could not save notes', variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const saveBday = async (clear = false) => {
    const value = clear ? '' : `${String(bday.month).padStart(2, '0')}-${String(bday.day).padStart(2, '0')}`;
    if (!clear && (!bday.day || !bday.month)) { toast({ title: 'Pick a day and month', variant: 'destructive' }); return; }
    try {
      await adminUpdateCrm(openId, { birthday: value });
      setProfile((p) => ({ ...p, profile: { ...p.profile, birthday: value } }));
      if (clear) setBday({ day: '', month: '' });
      toast({ title: clear ? 'Birthday cleared' : 'Birthday saved 🎂' });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not save birthday', variant: 'destructive' }); }
  };

  const addCredit = async () => {
    const amt = parseFloat(creditAmt);
    if (!amt) { toast({ title: 'Enter an amount (use minus to deduct)', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const res = await adminMemberCredit(openId, amt, creditNote || 'Manual adjustment by admin');
      setProfile((p) => ({ ...p, profile: { ...p.profile, credit: res.credit } }));
      setCreditAmt(''); setCreditNote('');
      toast({ title: `Credit updated — now ${money(res.credit)}` });
      load(); openProfile(openId);
    } catch (e) { toast({ title: 'Could not update credit', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const toggleBlock = async () => {
    const next = !profile.profile.blocked;
    if (!window.confirm(next ? "Block this account? They won't be able to sign in or use their account until unblocked." : 'Unblock this account?')) return;
    setBusy(true);
    try {
      await adminMemberBlock(openId, next);
      setProfile((p) => ({ ...p, profile: { ...p.profile, blocked: next } }));
      toast({ title: next ? 'Account blocked' : 'Account unblocked' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not update account', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const deleteMember = async () => {
    if (!window.confirm(`Permanently delete ${profile.profile.email}? Their account, login and credit are removed. Order history is kept for your records. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminMemberDelete(openId);
      toast({ title: 'Account deleted' });
      setOpenId(null);
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not delete account', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const now = Date.now();  const rows = useMemo(() => {
    if (seg === 'guests') return data.guests || [];
    let list = data.members || [];
    if (seg === 'vip') list = list.filter((m) => m.totalSpent >= 300);
    if (seg === 'credit') list = list.filter((m) => m.credit > 0);
    if (seg === 'new') list = list.filter((m) => m.createdAt && now - new Date(m.createdAt).getTime() < 30 * 864e5);
    if (seg === 'quiet') list = list.filter((m) => m.orderCount > 0 && m.lastOrderAt && now - new Date(m.lastOrderAt).getTime() > 60 * 864e5);
    const query = q.trim().toLowerCase();
    if (query) list = list.filter((m) => [m.fullName, m.email, m.phone, (m.tags || []).join(' ')].join(' ').toLowerCase().includes(query));
    return [...list].sort((a, b) => (b.lastOrderAt || '').localeCompare(a.lastOrderAt || ''));
  }, [data, seg, q, now]);

  if (loading) return <div className="py-20 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={22} /></div>;

  return (
    <div data-testid="crm-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><Users size={20} className="text-alp-gold" /> Customers</h2>
          <p className="text-alp-gray text-sm mt-1">{data.members.length} members &middot; {(data.guests || []).length} guest buyers. Tap anyone for their full story.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, tag..."
            data-testid="crm-search"
            className="w-full bg-alp-dark border border-alp-line rounded-full pl-9 pr-3 py-2 text-sm text-alp-cream placeholder-alp-gray focus:border-alp-gold outline-none" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SEGMENTS.map((s) => (
          <button key={s.key} onClick={() => setSeg(s.key)} data-testid={`crm-seg-${s.key}`}
            className={`shrink-0 px-4 py-1.5 rounded-full text-xs tracking-[0.1em] uppercase border transition-colors ${seg === s.key ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:text-alp-gold hover:border-alp-gold/60'}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="border border-alp-line rounded-lg overflow-hidden divide-y divide-alp-line/50 bg-alp-card/30">
        {rows.length === 0 ? (
          <p className="text-alp-gray text-sm text-center py-14" data-testid="crm-empty">No customers match.</p>
        ) : rows.map((m) => (
          <button key={m.id || m.email} onClick={() => m.id && openProfile(m.id)} disabled={!m.id}
            data-testid={`crm-row-${m.id || m.email}`}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${m.id ? 'hover:bg-alp-card/60' : 'opacity-80 cursor-default'}`}>
            <span className="w-9 h-9 rounded-full bg-alp-gold/15 border border-alp-gold/30 text-alp-gold flex items-center justify-center text-xs font-semibold shrink-0">
              {(m.fullName || m.email || '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-alp-cream text-sm truncate">{m.fullName || m.email}
                {(m.tags || []).map((t) => <Chip key={t} cls="ml-2 text-alp-gold border-alp-gold/40">{t}</Chip>)}
                {m.blocked && <Chip cls="ml-2 text-red-300 border-red-400/50">Blocked</Chip>}
                {!m.id && <Chip cls="ml-2 text-alp-gray border-alp-line">Guest</Chip>}
              </span>
              <span className="block text-alp-gray text-xs truncate">{m.email} {m.phone ? `· ${m.phone}` : ''}</span>
            </span>
            <span className="hidden sm:block text-right shrink-0">
              <span className="block text-alp-cream text-sm">{money(m.totalSpent)}</span>
              <span className="block text-alp-gray text-[11px]">{m.orderCount} order{m.orderCount === 1 ? '' : 's'}</span>
            </span>
            <span className="hidden md:block text-right shrink-0 w-24">
              <span className="block text-alp-gray text-[11px]">Last order</span>
              <span className="block text-alp-cream/80 text-xs">{dt(m.lastOrderAt)}</span>
            </span>
            {m.credit > 0 && <Chip cls="text-emerald-300 border-emerald-400/40 shrink-0">{money(m.credit)}</Chip>}
          </button>
        ))}
      </div>

      {/* Profile drawer */}
      {openId && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="crm-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpenId(null)} />
          <div className="relative w-full sm:max-w-lg h-full bg-alp-black border-l border-alp-line overflow-y-auto">
            {profileLoading || !profile ? (
              <div className="py-24 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={22} /></div>
            ) : (
              <div className="p-6 space-y-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-2xl text-alp-cream">{profile.profile.fullName || profile.profile.email}</h3>
                    <p className="text-alp-gray text-xs mt-1">{profile.profile.email}{profile.profile.phone ? ` · ${profile.profile.phone}` : ''} · joined {dt(profile.profile.createdAt)}</p>
                  </div>
                  <button onClick={() => setOpenId(null)} data-testid="crm-drawer-close" className="w-9 h-9 rounded-full border border-alp-line text-alp-gray hover:text-alp-gold hover:border-alp-gold flex items-center justify-center shrink-0"><X size={16} /></button>
                </div>

                <div className="flex gap-2">
                  {profile.profile.phone && (
                    <a href={`https://wa.me/${toAuWhatsApp(profile.profile.phone)}`} target="_blank" rel="noopener noreferrer" data-testid="crm-whatsapp"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 border border-emerald-500/40 text-emerald-300 rounded py-2 text-xs tracking-[0.1em] uppercase hover:bg-emerald-500/10 transition-colors"><MessageCircle size={13} /> WhatsApp</a>
                  )}
                  {profile.profile.phone && (
                    <a href={`sms:${profile.profile.phone}`} data-testid="crm-sms"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 border border-alp-line text-alp-cream rounded py-2 text-xs tracking-[0.1em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors"><Smartphone size={13} /> SMS</a>
                  )}
                  <a href={`mailto:${profile.profile.email}`} data-testid="crm-email"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-alp-line text-alp-cream rounded py-2 text-xs tracking-[0.1em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors"><Mail size={13} /> Email</a>
                </div>

                {profile.profile.blocked && (
                  <div className="border border-red-400/40 bg-red-500/10 rounded p-3 text-red-300 text-xs flex items-center gap-2" data-testid="crm-blocked-banner">
                    <ShieldOff size={14} /> This account is blocked — they cannot sign in.
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={toggleBlock} disabled={busy} data-testid="crm-block-btn"
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded py-2 text-xs tracking-[0.1em] uppercase border transition-colors disabled:opacity-50 ${profile.profile.blocked ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10' : 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'}`}>
                    {profile.profile.blocked ? <><ShieldOk size={13} /> Unblock account</> : <><ShieldOff size={13} /> Block account</>}
                  </button>
                  <button onClick={deleteMember} disabled={busy} data-testid="crm-delete-btn"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-red-500/40 text-red-300 rounded py-2 text-xs tracking-[0.1em] uppercase hover:bg-red-500/10 transition-colors disabled:opacity-50">
                    <Trash2 size={13} /> Delete account
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {[['Orders', profile.stats.orderCount], ['Spent', money(profile.stats.totalSpent)],
                    ['Avg order', money(profile.stats.avgOrder)], ['Credit', money(profile.profile.credit)]].map(([l, v]) => (
                    <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-3 text-center">
                      <p className="text-[9px] tracking-[0.15em] uppercase text-alp-gray">{l}</p>
                      <p className="text-alp-cream text-sm mt-1">{v}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><Tag size={12} /> Tags</p>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {(profile.profile.tags || []).map((t) => (
                      <button key={t} onClick={() => saveTags(profile.profile.tags.filter((x) => x !== t))} data-testid={`crm-tag-${t}`}
                        className="inline-flex items-center gap-1 text-[11px] text-alp-gold border border-alp-gold/40 rounded-full px-2.5 py-1 hover:bg-alp-gold/10">{t} <X size={10} /></button>
                    ))}
                    {TAG_SUGGESTIONS.filter((t) => !(profile.profile.tags || []).includes(t)).map((t) => (
                      <button key={t} onClick={() => addTag(t)} className="text-[11px] text-alp-gray border border-alp-line border-dashed rounded-full px-2.5 py-1 hover:text-alp-gold hover:border-alp-gold/50">+ {t}</button>
                    ))}
                    <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addTag(tagInput); }}
                      placeholder="Custom tag ⏎" data-testid="crm-tag-input"
                      className="bg-alp-dark border border-alp-line rounded-full px-3 py-1 text-[11px] text-alp-cream w-28 focus:border-alp-gold outline-none" />
                  </div>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><Gift size={12} /> Birthday {profile.profile.birthday && <span className="text-alp-cream/80 normal-case tracking-normal">· gets a 15% voucher on the day</span>}</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select value={bday.day} onChange={(e) => setBday((b) => ({ ...b, day: e.target.value }))} data-testid="crm-bday-day"
                      className="bg-alp-dark border border-alp-line rounded px-2.5 py-1.5 text-xs text-alp-cream focus:border-alp-gold outline-none">
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                    </select>
                    <select value={bday.month} onChange={(e) => setBday((b) => ({ ...b, month: e.target.value }))} data-testid="crm-bday-month"
                      className="bg-alp-dark border border-alp-line rounded px-2.5 py-1.5 text-xs text-alp-cream focus:border-alp-gold outline-none">
                      <option value="">Month</option>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <button onClick={() => saveBday(false)} data-testid="crm-bday-save"
                      className="px-3 py-1.5 bg-alp-gold text-alp-black rounded text-[11px] tracking-[0.1em] uppercase font-semibold hover:bg-alp-goldlight">Save</button>
                    {profile.profile.birthday && (
                      <button onClick={() => saveBday(true)} data-testid="crm-bday-clear"
                        className="px-3 py-1.5 border border-alp-line text-alp-gray rounded text-[11px] tracking-[0.1em] uppercase hover:border-alp-gold hover:text-alp-gold">Clear</button>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><Wallet size={12} /> Store credit</p>
                  <div className="flex gap-2">
                    <input type="number" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} placeholder="+/- $" data-testid="crm-credit-amount"
                      className="w-24 bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none" />
                    <input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Reason (they see this)" data-testid="crm-credit-note"
                      className="flex-1 bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none" />
                    <button onClick={addCredit} disabled={busy} data-testid="crm-credit-save"
                      className="px-4 bg-alp-gold text-alp-black rounded text-xs tracking-[0.1em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50"><Plus size={14} /></button>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><StickyNote size={12} /> Private notes</p>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="crm-notes"
                    placeholder="Preferences, conversations, anything to remember..."
                    className="w-full bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none resize-y" />
                  <button onClick={saveNotes} disabled={busy} data-testid="crm-notes-save"
                    className="mt-1 px-4 py-1.5 border border-alp-line text-alp-cream rounded text-xs tracking-[0.1em] uppercase hover:border-alp-gold hover:text-alp-gold disabled:opacity-50">Save notes</button>
                </div>

                {profile.affiliateCodes.length > 0 && (
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><BadgePercent size={12} /> Affiliate</p>
                    {profile.affiliateCodes.map((a) => (
                      <Chip key={a.code} cls="text-alp-gold border-alp-gold/40 mr-2">{a.code} · {a.commissionPercent}% {a.active ? '' : '· inactive'}</Chip>
                    ))}
                  </div>
                )}

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><Package size={12} /> Orders ({profile.stats.orderCount})</p>
                  <div className="border border-alp-line rounded-lg divide-y divide-alp-line/50 max-h-56 overflow-y-auto">
                    {profile.orders.length === 0 ? <p className="text-alp-gray text-xs p-4 text-center">No orders yet.</p>
                      : profile.orders.map((o) => (
                        <div key={o.orderNumber} className="px-3 py-2.5">
                          <div className="flex justify-between gap-2">
                            <span className="text-alp-gold text-xs">{o.orderNumber}</span>
                            <span className="text-alp-cream text-xs">{money(o.total)} · <span className={o.paymentStatus === 'Paid' ? 'text-emerald-300' : 'text-amber-300'}>{o.orderStatus}</span></span>
                          </div>
                          <p className="text-alp-gray text-[11px] truncate mt-0.5">{dt(o.createdAt)} · {o.itemsSummary}</p>
                        </div>
                      ))}
                  </div>
                </div>

                {profile.creditHistory.length > 0 && (
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">Credit ledger</p>
                    <div className="border border-alp-line rounded-lg divide-y divide-alp-line/50 max-h-40 overflow-y-auto">
                      {profile.creditHistory.map((t, i) => (
                        <div key={i} className="px-3 py-2 flex justify-between gap-2 text-xs">
                          <span className="text-alp-cream/80 truncate">{t.note || (t.amount >= 0 ? 'Credit added' : 'Credit used')}</span>
                          <span className={t.amount >= 0 ? 'text-emerald-300' : 'text-red-300'}>{t.amount >= 0 ? '+' : '−'}{money(Math.abs(t.amount)).slice(0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {profile.messages.length > 0 && (
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">Messages</p>
                    {profile.messages.map((mg, i) => (
                      <div key={i} className="border border-alp-line rounded-lg p-3 mb-2">
                        <p className="text-alp-cream/80 text-xs">{mg.message}</p>
                        <p className="text-alp-gray text-[10px] mt-1">{dt(mg.createdAt)} · {mg.replied ? 'Replied' : 'Awaiting reply'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
